<div class="invoice-detail min-h-full bg-[var(--bg-color)] text-[var(--f-color)]">
  <app-header
    [title]="'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.HEADER' | translate"
    [subtitle]="(headerView$ | async)?.number || ''"
  >
    <div header-actions class="hidden lg:block">
      <app-navbar-profile-info></app-navbar-profile-info>
    </div>
  </app-header>

  <div class="page-content invoice-detail__content relative px-4 py-6 md:px-8 md:py-8">
    <div class="invoice-detail__topbar invoice-detail__no-print">
      <app-back-button [fallbackUrl]="salesOrderBackUrl" />
      <div class="invoice-detail__topbar-actions">
        <button type="button" class="form-secondary-btn" (click)="goBack()">
          {{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.BACK_BUTTON' | translate }}
        </button>
        <button type="button" class="form-primary-btn" (click)="print()">
          {{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.PRINT_BUTTON' | translate }}
        </button>
      </div>
    </div>

    @if (isLoading) {
      <div class="invoice-detail__loading invoice-detail__no-print" role="status" aria-live="polite">
        {{ 'MESSAGES.LOADING' | translate }}
      </div>
    } @else if (pageErrorKey) {
      <app-card-shell padding="lg">
        <div class="invoice-detail__error invoice-detail__no-print">
          <h2 class="invoice-detail__error-title">{{ 'ERRORS.UNKNOWN_ERROR' | translate }}</h2>
          <p class="invoice-detail__error-message">{{ pageErrorKey | translate }}</p>
          <button type="button" class="form-secondary-btn" (click)="retry()">
            {{ 'BUTTONS.RETRY' | translate }}
          </button>
        </div>
      </app-card-shell>
    } @else if (headerView$ | async; as header) {
      <article class="invoice-detail__sheet mx-auto flex max-w-5xl flex-col gap-6">
        <app-card-shell padding="lg">
          <header class="invoice-detail__header">
            <div class="invoice-detail__header-titles">
              <h1 class="invoice-detail__title">{{ header.number }}</h1>
              <span class="invoice-detail__status-badge">
                {{ header.statusLabelKey | translate }}
              </span>
            </div>

            <dl class="invoice-detail__header-meta">
              <div>
                <dt>{{ 'COMPONENTS.SALES_ORDERS.INVOICES.INVOICE_DATE' | translate }}</dt>
                <dd>{{ header.invoiceDate | localizedDate }}</dd>
              </div>
              <div>
                <dt>{{ 'COMPONENTS.SALES_ORDERS.INVOICES.DUE_DATE' | translate }}</dt>
                <dd>{{ header.dueDate | localizedDate }}</dd>
              </div>
              <div>
                <dt>{{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.CUSTOMER' | translate }}</dt>
                <dd>{{ header.customerId }}</dd>
              </div>
            </dl>
          </header>
        </app-card-shell>

        <app-card-shell padding="none">
          @if (lineViews$ | async; as lineViews) {
            <table class="invoice-detail__lines">
              <thead>
                <tr>
                  <th class="invoice-detail__line-cell--text">
                    {{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.LINE_TABLE.DESCRIPTION' | translate }}
                  </th>
                  <th class="invoice-detail__line-cell--right">
                    {{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.LINE_TABLE.QUANTITY' | translate }}
                  </th>
                  <th class="invoice-detail__line-cell--right">
                    {{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.LINE_TABLE.UOM' | translate }}
                  </th>
                  <th class="invoice-detail__line-cell--right">
                    {{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.LINE_TABLE.UNIT_PRICE' | translate }}
                  </th>
                  <th class="invoice-detail__line-cell--right">
                    {{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.LINE_TABLE.TAX_PERCENT' | translate }}
                  </th>
                  <th class="invoice-detail__line-cell--right">
                    {{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.LINE_TABLE.AMOUNT_EXCL' | translate }}
                  </th>
                  <th class="invoice-detail__line-cell--right">
                    {{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.LINE_TABLE.TAX' | translate }}
                  </th>
                  <th class="invoice-detail__line-cell--right">
                    {{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.LINE_TABLE.AMOUNT_INCL' | translate }}
                  </th>
                </tr>
              </thead>
              <tbody>
                @for (line of lineViews; track line.id) {
                  <tr>
                    <td class="invoice-detail__line-cell--text">{{ line.description || '-' }}</td>
                    <td class="invoice-detail__line-cell--right">{{ line.quantity | number: '1.0-2' }}</td>
                    <td class="invoice-detail__line-cell--right">{{ line.unitOfMeasureId }}</td>
                    <td class="invoice-detail__line-cell--right">{{ line.unitPrice | number: '1.2-2' }}</td>
                    <td class="invoice-detail__line-cell--right">{{ line.taxPercent | number: '1.0-2' }}</td>
                    <td class="invoice-detail__line-cell--right">{{ line.amountExcludingTax | number: '1.2-2' }}</td>
                    <td class="invoice-detail__line-cell--right">{{ line.totalTaxAmount | number: '1.2-2' }}</td>
                    <td class="invoice-detail__line-cell--right">{{ line.amountIncludingTax | number: '1.2-2' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </app-card-shell>

        <app-card-shell padding="lg">
          <section class="invoice-detail__totals">
            <div class="invoice-detail__total-row">
              <span>{{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.TOTALS.SUBTOTAL' | translate }}</span>
              <span>{{ header.subtotal | number: '1.2-2' }}</span>
            </div>
            <div class="invoice-detail__total-row">
              <span>{{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.TOTALS.TAX_TOTAL' | translate }}</span>
              <span>{{ header.taxTotal | number: '1.2-2' }}</span>
            </div>
            <div class="invoice-detail__total-row invoice-detail__total-row--accent">
              <span>{{ 'COMPONENTS.SALES_ORDERS.INVOICES.DETAIL.TOTALS.TOTAL' | translate }}</span>
              <span>{{ header.total | number: '1.2-2' }}</span>
            </div>
          </section>
        </app-card-shell>
      </article>
    }
  </div>
</div>
